import { LOCATIONS } from "@/data/locations";

// Stylized (not-to-scale) Southwest Florida coverage map. Decorative —
// clickable city links live in the LocationCards beside it.
const CITY_POS = {
    "cape-coral": { x: 148, y: 92, lx: 140, ly: 74, anchor: "start", tagDy: -12 },
    "fort-myers": { x: 220, y: 122, lx: 232, ly: 127, anchor: "start", tagDy: 13 },
    "lehigh-acres": { x: 330, y: 104, lx: 336, ly: 88, anchor: "end", tagDy: -12 },
    "bonita-springs": { x: 224, y: 226, lx: 236, ly: 231, anchor: "start", tagDy: 13 },
    naples: { x: 196, y: 342 },
};

export default function SwflMap({ highlightSlug }) {
    const naples = CITY_POS.naples;
    const cities = LOCATIONS.filter((l) => !l.is_primary && CITY_POS[l.slug]);

    return (
        <div className="swfl-map-card" data-testid="swfl-map">
            <svg
                viewBox="0 0 420 470"
                role="img"
                aria-label="Map of A1 Screening's Southwest Florida service area — Naples home base, plus Cape Coral, Fort Myers, Lehigh Acres, and Bonita Springs"
                style={{ width: "100%", height: "auto", display: "block" }}
            >
                <defs>
                    <pattern id="swflMesh" width="16" height="16" patternUnits="userSpaceOnUse">
                        <path d="M16 0H0V16" fill="none" stroke="rgba(40,216,224,0.07)" strokeWidth="1" />
                    </pattern>
                </defs>

                {/* land + screen-mesh texture */}
                <rect x="0" y="0" width="420" height="470" fill="url(#swflMesh)" />

                {/* gulf water */}
                <path
                    d="M118 0 C104 55 128 100 118 150 C108 196 138 232 146 282 C153 322 148 362 172 402 C186 430 208 450 240 470 L0 470 L0 0 Z"
                    fill="rgba(40,216,224,0.05)"
                />
                {/* coastline */}
                <path
                    d="M118 0 C104 55 128 100 118 150 C108 196 138 232 146 282 C153 322 148 362 172 402 C186 430 208 450 240 470"
                    fill="none"
                    stroke="rgba(40,216,224,0.55)"
                    strokeWidth="2"
                    strokeLinecap="round"
                />
                {/* I-75 corridor */}
                <path
                    d="M258 0 C250 90 262 170 252 250 C244 320 250 400 262 470"
                    fill="none"
                    stroke="rgba(255,255,255,0.12)"
                    strokeWidth="2.5"
                    strokeDasharray="1 7"
                    strokeLinecap="round"
                />
                <text x="270" y="24" fill="rgba(255,255,255,0.35)" fontSize="10" fontWeight="700" letterSpacing="1">I-75</text>
                <text
                    x="46" y="300"
                    fill="rgba(40,216,224,0.4)"
                    fontSize="11"
                    fontWeight="700"
                    letterSpacing="4"
                    transform="rotate(-78 46 300)"
                >
                    GULF OF MEXICO
                </text>

                {/* service routes out of Naples */}
                {cities.map((c) => {
                    const p = CITY_POS[c.slug];
                    return (
                        <line
                            key={`route-${c.slug}`}
                            x1={naples.x} y1={naples.y} x2={p.x} y2={p.y}
                            stroke="rgba(40,216,224,0.22)"
                            strokeWidth="1.4"
                            strokeDasharray="3 5"
                        />
                    );
                })}

                {/* secondary cities */}
                {cities.map((c) => {
                    const p = CITY_POS[c.slug];
                    const hl = c.slug === highlightSlug;
                    return (
                        <g key={c.slug}>
                            {hl ? (
                                <circle className="swfl-pulse" cx={p.x} cy={p.y} r="11" fill="none" stroke="var(--color-accent, #28d8e0)" strokeWidth="2" />
                            ) : null}
                            <circle
                                cx={p.x} cy={p.y} r={hl ? 6.5 : 5}
                                fill={hl ? "var(--color-accent, #28d8e0)" : "#0a1426"}
                                stroke="var(--color-accent, #28d8e0)" strokeWidth="2"
                            />
                            <text x={p.lx} y={p.ly} textAnchor={p.anchor} fill={hl ? "#ffffff" : "rgba(255,255,255,0.88)"} fontSize={hl ? "13.5" : "12.5"} fontWeight={hl ? "800" : "700"}>
                                {c.name}
                            </text>
                            {hl ? (
                                <text x={p.lx} y={p.ly + p.tagDy} textAnchor={p.anchor} fill="var(--color-accent, #28d8e0)" fontSize="9" fontWeight="800" letterSpacing="2">
                                    SERVICE AREA
                                </text>
                            ) : null}
                        </g>
                    );
                })}

                {/* Naples — home base */}
                <g>
                    <circle className="swfl-pulse" cx={naples.x} cy={naples.y} r="12" fill="none" stroke="var(--color-cta, #fcd64b)" strokeWidth="2" />
                    <circle cx={naples.x} cy={naples.y} r="7" fill="var(--color-cta, #fcd64b)" stroke="#0a1426" strokeWidth="2" />
                    <text x={naples.x + 15} y={naples.y + 1} fill="#ffffff" fontSize="15" fontWeight="800">Naples</text>
                    <text x={naples.x + 15} y={naples.y + 16} fill="var(--color-cta, #fcd64b)" fontSize="9.5" fontWeight="800" letterSpacing="2">HOME BASE</text>
                </g>
            </svg>
            <div className="flex items-center gap-4 mt-2 px-1 flex-wrap">
                <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    <span style={{ color: "var(--color-cta, #fcd64b)" }}>●</span> Home base
                </span>
                <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    <span style={{ color: "var(--color-accent, #28d8e0)" }}>●</span> Service area
                </span>
                <span className="text-xs ml-auto" style={{ color: "var(--color-text-muted)" }}>
                    Not to scale
                </span>
            </div>
        </div>
    );
}
